/**
 * Request information about the selected enterprise that doesn't change very
 * often.
 */
import axios from 'axios';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import { SagaIterator } from 'redux-saga';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';

import { MESSAGES } from 'clients/utils/parse-axios-error';
import { setDevices } from 'store/modules/devices/actions';
import { setSamplePoints } from 'store/modules/samplePoints/actions';
import { loadSamplePointsStatistics } from 'store/modules/samplePointsStatistics/actions';
import { setSites } from 'store/modules/sites/actions';
import { setTriggers } from 'store/modules/triggers/actions';
import { setUsers } from 'store/modules/users/actions';
import { RawAssetTypeCode } from 'types/models/asset-type';
import SamplePoint, { RawSamplePoint, SamplePointId, SensorTagId } from 'types/models/samplePoint';
import Site from 'types/models/site';
import User from 'types/models/user';
import { parseRawSamplePoints } from 'utils/associated-sample-points/parse-raw-samplepoints';
import parseUserPhones from 'utils/phone-number/parse-user-phones';
import { getRequest } from 'utils/redux-saga-requests';

import getMockMachineControls from './__tests__/fixtures/getMockMachineControls';
import {
  loadEnterpriseScaffold,
  loadEnterpriseScaffoldFailure,
  loadEnterpriseScaffoldSuccess
} from './actions';
import ActionTypes from './constants';
import { selectCurrentEnterpriseCountry } from '../enterprise/selectors';
import { fetchEventReasonCatalog } from '../eventReasons/actions';
import { selectIsEventReasonCatalogLoaded } from '../eventReasons/selectors';
import { selectDefaultSite } from '../localUserSettings/selectors';
import { loadRecentUnacknowledgedNotifications } from '../notifications/actions';
import { parseSites } from '../sites/sagaHelper';
import trackEvent from '../tracking/actions';
import { EventType } from '../tracking/types';

function convertUnits(
  samplePoints: Record<SamplePointId, SamplePoint>
): Record<SamplePointId, SamplePoint> {
  return mapValues(samplePoints, (sp) => {
    const samplePoint = { ...sp };
    if (samplePoint.config?.maxValue) {
      samplePoint.config.maxValue = +samplePoint.config.maxValue;
    }
    if (samplePoint.config?.totalCapacity) {
      samplePoint.config.totalCapacity = +samplePoint.config.totalCapacity;
    }

    return samplePoint;
  });
}

function isSpectrumSoilMoistureAboveLimit(sp: RawSamplePoint) {
  const isSpectrumSoilMoisture = (
    sp.assetTypeId === RawAssetTypeCode.SOIL_MOISTURE &&
    sp.sensorTags.id === SensorTagId.SPECTRUM_WEATHER_STATION_SOIL_MOISTURE
  );
  const spectrumSoilMoistureValueLimit = 100;
  const lastSampleValue = sp.lastSampleData?.rwValue ?? 0;
  return isSpectrumSoilMoisture && lastSampleValue > spectrumSoilMoistureValueLimit;
}

export function* requestEnterpriseScaffold(
  action: ReturnType<typeof loadEnterpriseScaffold>
): SagaIterator {
  const {
    payload: {
      enterpriseId,
      excludedAssetTypes,
      enableCriticalLowTankTroughAlertReasonTagging,
      isInitialLoading,
      onInitialLoadingSuccess
    }
  } = action;

  try {
    const { data } = yield call(getRequest, `enterprise/${enterpriseId}/core`);
    const { devices, sites, triggers, users, enterprise } = data;
    let samplePoints: RawSamplePoint[] = data.samplepoints;
    // Testing only. It works with mocks in 1) store/modules/samplePointsStatistics/saga.ts
    // 2) components/features/machineControl/MachineControlPanel/index.tsx
    let machineControlTestingEnterpriseId = 779;
    let machineControlTestingSiteId = 722;
    let machineControlTestingDeviceId = 32694;
    if (import.meta.env.VITE_ENV === 'staging') {
      machineControlTestingEnterpriseId = 1755;
      machineControlTestingSiteId = 4330;
      machineControlTestingDeviceId = 30281;
    }
    if (
      import.meta.env.VITE_ENV !== 'production' &&
      enterprise.id === machineControlTestingEnterpriseId
    ) {
      samplePoints = samplePoints.concat(
        // @ts-ignore TODO: the return type of getMockMachineControls is not compatible with RawSamplePoint[]
        getMockMachineControls(
          machineControlTestingDeviceId,
          machineControlTestingSiteId
        )
      );
    }
    const country = yield select(selectCurrentEnterpriseCountry);
    if (excludedAssetTypes.length) {
      samplePoints = samplePoints.filter(sp => !excludedAssetTypes.includes(sp.assetTypeId));
    }

    // @debt Ideally, this should be fixed in IoT or nearer at the hardware level.
    // We're also using a deprecated field `samplePoint.lastSampleData` inside isSpectrumSoilMoistureAboveLimit().
    // This drops Spectrum Soil Moisture sample points whose latest reading exceeds the valid limit (100%).
    // This is just a temporary FE workaround for a port configuration issue in the Spectrum WeatherStation.
    samplePoints = samplePoints.filter(sp => !isSpectrumSoilMoistureAboveLimit(sp));

    const parsedSamplePoints = parseRawSamplePoints(samplePoints, country);
    let samplePointsDictionary = keyBy(parsedSamplePoints, 'id');
    samplePointsDictionary = convertUnits(samplePointsDictionary);

    const devicesData = keyBy(devices, 'id');
    const triggersData = keyBy(triggers, 'id');
    const parsedSites = parseSites(sites, country);
    const sitesDict = keyBy(parsedSites, 'id');
    const usersData = keyBy(
      users.map((user: User): User => parseUserPhones(user)),
      'id'
    );

    yield all([
      put(setSites(sitesDict)),
      put(setDevices(devicesData)),
      put(setSamplePoints(samplePointsDictionary)),
      put(setTriggers(triggersData)),
      put(setUsers(usersData)),
      put(loadEnterpriseScaffoldSuccess())
    ]);

    yield put(loadRecentUnacknowledgedNotifications(enterprise.id));

    // Fetch catalog for alert reason tagging
    const isCatalogLoaded = yield select(selectIsEventReasonCatalogLoaded);
    if (enableCriticalLowTankTroughAlertReasonTagging && !isCatalogLoaded) {
      yield put(fetchEventReasonCatalog());
    }

    /**
     * FMBT-978 To make sure the sample points and their statuses are synced.
     * ref: https://farmbot.atlassian.net/browse/FMBT-978
     */
    yield put(loadSamplePointsStatistics(enterpriseId));
    if (isInitialLoading) {
      const sensorTypes = parsedSamplePoints.reduce(
        (acc: number[], sp: SamplePoint) => {
          if (!acc.includes(sp.assetTypeId)) {
            acc.push(sp.assetTypeId);
          }
          return acc;
        },
        []
      );
      onInitialLoadingSuccess?.({
        kind: 'enterprise',
        key: enterprise.id,
        name: enterprise.name,
        industry: enterprise.industry,
        sensorTypes,
        numberOfDevices: devices.length,
        numberOfSites: sites.length,
        numberOfSensors: samplePoints.length,
        country,
        createdAt: enterprise.createdAt
      });
      yield put(
        trackEvent({
          type: EventType.SET_ENTERPRISE,
          data: {
            groupType: 'company',
            groupValue: enterprise.name,
            name: enterprise.name,
            industry: enterprise.industry,
            numberOfSites: sites.length,
            numberOfSensors: samplePoints.length,
            createdAt: new Date(enterprise.createdAt).toISOString()
          }
        })
      );
      const currentSite: Site = yield select(selectDefaultSite);
      yield put(
        trackEvent({
          type: EventType.SET_SITE,
          data: {
            groupType: 'site',
            groupValue: `${enterprise?.name} - ${currentSite.name}`,
            name: currentSite.name,
            id: currentSite.sid,
            numberOfSensors: parsedSamplePoints.filter(
              (sp: SamplePoint) => sp.siteId === currentSite.id
            )?.length,
            siteSize: currentSite.siteSize
          }
        })
      );
    }
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    const message = error.message === MESSAGES.NETWORK_ERROR
      ? error.message
      : get(error, 'response.data.message', 'Failed to fetch enterprise core data');
    yield put(loadEnterpriseScaffoldFailure(message, error));
  }
}

export function* watchLoadEnterpriseScaffoldRequest() {
  yield takeLatest(
    ActionTypes.LOAD_ENTERPRISE_SCAFFOLD_REQUEST,
    requestEnterpriseScaffold
  );
}

export default function* EnterpriseScaffoldSaga() {
  yield all([fork(watchLoadEnterpriseScaffoldRequest)]);
}
